import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Pagination,
  LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ContentCopy as CopyIcon,
  NfcRounded,
  OpenInNew,
  CheckCircleOutline,
  RadioButtonUnchecked,
  TouchAppOutlined,
  LinkRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C'; // eslint-disable-line no-unused-vars
const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.08)';
const PAGE_SIZE = 7;

const normalizeNfcList = (list) =>
  (list || []).map((c) => ({
    ...c,
    editType: c.target_type === 'vcard' ? 'vcard' : 'card',
    editValue: c.target_value || '',
  }));

const NfcManager = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const firstLoadRef = useRef(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState(null);
  const [myCardSlugs, setMyCardSlugs] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchNfcPage = async (p) => {
    setPage(p);
    try {
      setError(null);
      if (firstLoadRef.current) setLoading(true);
      else setListLoading(true);
      const { data } = await api.get('/nfc/my-cards', { params: { page: p, limit: PAGE_SIZE } });
      let list = data?.cards || [];
      let pag = data?.pagination;
      let resolvedPage = p;
      if (p > 1 && list.length === 0 && pag && (pag.total || 0) > 0) {
        resolvedPage = Math.max(1, p - 1);
        setPage(resolvedPage);
        const { data: d2 } = await api.get('/nfc/my-cards', { params: { page: resolvedPage, limit: PAGE_SIZE } });
        list = d2?.cards || [];
        pag = d2?.pagination;
      }
      setCards(normalizeNfcList(list));
      setPagination(pag || null);
      setPage(resolvedPage);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setListLoading(false);
      firstLoadRef.current = false;
    }
  };

  useEffect(() => { fetchNfcPage(1); }, []);

  const saveCard = async (uid, payload) => {
    await api.put(`/nfc/cards/${uid}`, payload);
  };

  const handleUpdate = async (idx) => {
    const c = cards[idx];
    try {
      if (!c.editValue) throw new Error('Please select a card');
      await saveCard(c.uid, { targetType: c.editType, targetValue: c.editValue });
      await fetchNfcPage(page);
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const fetchOwnCardSlugs = async () => {
    try {
      const { data } = await api.get('/cards/my-cards');
      const slugs = (data?.cards || []).map(r => r.card_url || r.cardUrl).filter(Boolean);
      setMyCardSlugs(slugs);
    } catch {}
  };

  useEffect(() => { fetchOwnCardSlugs(); }, []);

  const computeResolveUrl = (type, slug) => {
    if (!slug) return '';
    if (type === 'vcard') return `https://api.webcard.digital/api/vcard/${slug}`;
    return `https://app.webcard.digital/${slug}`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Copy failed', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="body1" color="text.secondary">Loading NFC cards…</Typography>
      </Box>
    );
  }

  const totalNfc = pagination?.total ?? cards.length;

  if (error && !cards.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="body1" color="error.main">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default', py: 4, px: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            NFC Cards
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your NFC cards and configure what happens when they are tapped
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/nfc/my-print-designs')}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}
          >
            My print designs
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/nfc/print-designs')}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}
          >
            Create cards
          </Button>
        </Box>
      </Box>

      {/* Empty state */}
      {!loading && !listLoading && totalNfc === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <NfcRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
            No NFC Cards Found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 400, mx: 'auto' }}>
            Your NFC cards will appear here once they are registered to your account.
          </Typography>
        </Box>
      )}

      {/* Card list */}
      {listLoading && <LinearProgress sx={{ mb: 2, maxWidth: 960, borderRadius: 1 }} />}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        maxWidth: 960,
        opacity: listLoading ? 0.55 : 1,
        transition: 'opacity 0.2s',
      }}>
        {cards.map((c, idx) => {
          const destUrl = computeResolveUrl(c.editType, c.editValue);
          const nfcLink = `https://app.webcard.digital/uid/${c.uid}`;
          const isDirty = (c.target_type !== (c.editType ?? 'card')) || (c.target_value !== (c.editValue ?? ''));

          return (
            <Card key={c.uid} sx={{
              boxShadow: CARD_SHADOW,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: `4px solid ${c.is_active ? PRIMARY : '#9CA3AF'}`,
              bgcolor: 'background.paper',
              overflow: 'visible',
            }}>
              <CardContent sx={{ p: 0 }}>
                {/* Card header */}
                <Box sx={{
                  px: 3, py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 40, height: 40,
                      borderRadius: 1,
                      bgcolor: `${PRIMARY}0A`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <NfcRounded sx={{ fontSize: 22, color: PRIMARY }} />
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', fontFamily: 'monospace', fontSize: '14px' }}>
                        {c.uid}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TouchAppOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {c.tap_count} taps
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {c.is_active
                      ? <CheckCircleOutline sx={{ fontSize: 16, color: '#10B981' }} />
                      : <RadioButtonUnchecked sx={{ fontSize: 16, color: '#9CA3AF' }} />
                    }
                    <Typography variant="body2" sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: c.is_active ? '#10B981' : 'text.secondary',
                    }}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                </Box>

                {/* Configuration */}
                <Box sx={{ px: 3, py: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end', mb: 2.5 }}>
                    <TextField
                      select
                      label="Target Type"
                      size="small"
                      value={c.editType ?? 'card'}
                      onChange={(e) => setCards(prev => prev.map((p, i) => i === idx ? { ...p, editType: e.target.value } : p))}
                      sx={{ minWidth: 170, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                    >
                      <MenuItem value="card">Card URL</MenuItem>
                      <MenuItem value="vcard">vCard (download)</MenuItem>
                    </TextField>

                    <TextField
                      select
                      label="Target Card"
                      size="small"
                      value={c.editValue ?? ''}
                      onChange={(e) => setCards(prev => prev.map((p, i) => i === idx ? { ...p, editValue: e.target.value } : p))}
                      sx={{ minWidth: 220, flex: 1, maxWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                    >
                      {myCardSlugs.map(slug => (
                        <MenuItem key={slug} value={slug}>{slug}</MenuItem>
                      ))}
                    </TextField>

                    <Button
                      variant="contained"
                      onClick={() => handleUpdate(idx)}
                      disabled={!isDirty}
                      size="small"
                      sx={{
                        height: 40,
                        px: 3,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                      }}
                    >
                      Save
                    </Button>
                  </Box>

                  {/* Links section — grey.50 is always light in our palette; use theme-aware fill for dark mode */}
                  <Box sx={{
                    bgcolor: (t) => (t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.08) : t.palette.grey[50]),
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    {destUrl && (
                      <LinkRow
                        label="Destination"
                        url={destUrl}
                        onCopy={() => copyToClipboard(destUrl)}
                      />
                    )}
                    <LinkRow
                      label="NFC Link"
                      url={nfcLink}
                      onCopy={() => copyToClipboard(nfcLink)}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {pagination && pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, maxWidth: 960 }}>
          <Pagination
            color="primary"
            count={pagination.totalPages}
            page={page}
            onChange={(_, p) => fetchNfcPage(p)}
            shape="rounded"
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 1 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const LinkRow = ({ label, url, onCopy }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <LinkRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
    <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, minWidth: 80 }}>
      {label}
    </Typography>
    <Typography
      component="a"
      href={url}
      target="_blank"
      rel="noreferrer"
      variant="caption"
      sx={{
        color: PRIMARY,
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: 1,
      }}
    >
      {url}
    </Typography>
    <Tooltip title={`Copy ${label.toLowerCase()}`} arrow>
      <IconButton size="small" onClick={onCopy} sx={{ p: 0.5, color: 'text.secondary' }}>
        <CopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="Open in new tab" arrow>
      <IconButton size="small" component="a" href={url} target="_blank" rel="noreferrer" sx={{ p: 0.5, color: 'text.secondary' }}>
        <OpenInNew sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

export default NfcManager;
